<template>
  <div class="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
    <div
      v-for="(project, index) in projects"
      :key="project.name"
      class="product-card group relative flex flex-col rounded-lg p-5"
      :ref="el => setProjectCardRef(el, index)"
    >
      <h3 class="mb-2 text-lg font-extrabold text-white">{{ project.name }}</h3>
      <p class="mb-4 flex-1 text-sm leading-relaxed text-gray-400">{{ project.description }}</p>

      <div class="mb-4 flex flex-wrap gap-1.5">
        <span v-for="tag in project.stack" :key="tag" class="tag-chip">{{ tag }}</span>
      </div>

      <a
        :href="project.url"
        :class="['product-cta inline-flex items-center justify-center gap-2 text-sm font-bold', project.isActive ? '' : 'disabled-link']"
        :target="project.isActive ? '_blank' : undefined"
        :rel="project.isActive ? 'noopener noreferrer' : undefined"
        :aria-label="`Visit ${project.name}${project.isActive ? '' : ' (currently unavailable)'}`"
      >
        Visit Product
        <i class="fas fa-arrow-right cta-arrow text-xs"></i>
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { gsap } from 'gsap';
import { PROJECTS } from '../data/projects.js';

const projects = ref(PROJECTS);
const projectCards = ref([]);

const setProjectCardRef = (el, index) => {
  if (el) projectCards.value[index] = el;
};

onMounted(() => {
  gsap.from(projectCards.value, {
    y: 50,
    opacity: 0,
    stagger: 0.15,
    duration: 0.8,
    ease: 'power2.out',
  });
});
</script>

<style scoped>
@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.product-card {
  border: 2px solid #2dd4bf;
  background: #111827;
  box-shadow: 5px 5px 0 0 #2dd4bf;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: fadeInUp 0.5s ease-out forwards;
}

.product-card:hover {
  transform: translate(-3px, -3px);
  box-shadow: 8px 8px 0 0 #2dd4bf;
}

.tag-chip {
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  padding: 0.15rem 0.55rem;
  border-radius: 0.3rem;
  font-size: 0.68rem;
  font-weight: 600;
  color: #d1d5db;
}

.product-cta {
  padding: 0.65rem 1.1rem;
  border: 2px solid rgba(0, 0, 0, 0.4);
  border-radius: 0.4rem;
  background: #2dd4bf;
  color: #0f172a;
  box-shadow: 3px 3px 0 0 rgba(0, 0, 0, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.product-cta:hover {
  transform: translate(-2px, -2px);
  box-shadow: 5px 5px 0 0 rgba(0, 0, 0, 0.5);
}

.product-cta:active {
  transform: translate(1px, 1px);
  box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.5);
}

.cta-arrow {
  transition: transform 0.25s ease;
}

.product-cta:hover .cta-arrow {
  transform: translateX(3px);
}

.disabled-link {
  background-color: #374151 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

@media (prefers-reduced-motion: reduce) {
  .product-card,
  .product-cta,
  .cta-arrow {
    animation: none;
    transition: none;
  }
}
</style>
